import { useRef } from 'react';
import { useSetAtom } from 'jotai';
import { useCallbackOne as useStableCallback } from 'use-memo-one';
import { useDrawer } from '../../../../../common/multi-level-drawer';
import { useAtomEffect } from '../../../../../modernization-common/hooks/useAtomEffect';
import { useSidePanel } from '../../../../../modernization-common/side-panel';
import { SIDE_PANEL_ACTIONS } from '../../../../../modernization-common/side-panel/const';
import { ANNOTATION_TYPE } from '../../../../labelling/constants';
import { EDITOR_MODE_TYPE } from '../../../../labelling/types';
import { annotationLabelsAtom, isVideoTaskAtom } from '../../../atoms/annotation-atoms';
import { annotationIdToSpanAtom } from '../../../atoms/side-panel-atoms';
import { editorModeAtom } from '../../../atoms/stage-atoms';
import { draftFrameClassificationAtom, selectedAnnotationIdsAtom, showAnnotationDetailsAtom } from '../../../atoms/tool-atoms';
import { editAnnotationTriggerAtom, sidePanelDispatchAtom } from '../side-panel';
import { selectPanelToOpen } from './drawer-panel-config';
export function useDrawerManager() {
  const {
    open,
    close
  } = useDrawer();
  const {
    annotationsAtom
  } = useSidePanel();
  const dispatch = useSetAtom(sidePanelDispatchAtom);
  const openedRef = useRef<Set<string>>(new Set());
  useAtomEffect(useStableCallback(get => {
    const showAnnotationDetails = get(showAnnotationDetailsAtom);
    const editorMode = get(editorModeAtom);
    const selectedAnnotationIds = get(selectedAnnotationIdsAtom);
    const isVideoTask = get(isVideoTaskAtom);
    const annotationLabels = get(annotationLabelsAtom);
    const annotationIdToSpan = get(annotationIdToSpanAtom);
    const annotations = get(annotationsAtom);
    const editTrigger = get(editAnnotationTriggerAtom);
    const draftFrameClassification = get(draftFrameClassificationAtom);
    if (!showAnnotationDetails) {
      if (editorMode === EDITOR_MODE_TYPE.RELATIONSHIP) {
        return;
      }
      if (openedRef.current.size > 0) {
        close();
        openedRef.current.clear();
      }
      return;
    }
    const selectedId = selectedAnnotationIds[0];
    if (!selectedId) return;
    const draftAnnotation = draftFrameClassification && draftFrameClassification.annotationId === selectedId ? {
      id: selectedId,
      annotationType: ANNOTATION_TYPE.FRAME_CLASSIFICATION,
      annotationLabel: draftFrameClassification.annotationLabel,
      metadata: undefined
    } : undefined;
    const annotation = annotations.find(ann => ann.id === selectedId) ?? draftAnnotation;
    if (!annotation) {
      close();
      openedRef.current.delete(selectedId);
      return;
    }
    if (openedRef.current.has(selectedId)) return;
    const rangeSpans = annotationIdToSpan[selectedId];

    // Do not call close() here — closeDrawerAtom already pops the drawer before invoking this callback.
    const handleClose = () => {
      openedRef.current.delete(selectedId);
      dispatch({
        type: SIDE_PANEL_ACTIONS.EDIT_ANNOTATION_END
      });
    };
    const annotationName = `${annotation.annotationLabel.title.charAt(0).toUpperCase() + annotation.annotationLabel.title.slice(1)} [${annotation.id}]`;
    const isEditButtonClicked = editTrigger === selectedId;
    const selectedAnnotationDetails = {
      id: selectedId,
      annotationType: annotation.annotationType,
      annotationLabel: annotation.annotationLabel,
      metadata: {
        answers: annotation.metadata?.answers
      }
    };
    const drawerToOpen = selectPanelToOpen({
      selectedId,
      annotation,
      isVideoTask,
      rangeSpan: rangeSpans?.[0],
      isEditButtonClicked,
      annotationName,
      selectedAnnotationDetails,
      annotationLabels,
      handleClose,
      sidePanelDispatchAtom
    });
    if (!drawerToOpen) {
      openedRef.current.delete(selectedId);
      return;
    }
    openedRef.current.add(selectedId);
    open(drawerToOpen);
  }, []));
}